import { withCmsReadRetry } from '~/utils/cms-query-retry'
import type { GraphQLResponse, GraphQLQueryOptions } from '~/types/graphql'

export function useGraphQL() {
  const config = useRuntimeConfig()

  const query = async <T = any>(
    query: string, 
    variables: Record<string, any> = {}, 
    options: GraphQLQueryOptions = {}
  ): Promise<T> => {
    return withCmsReadRetry(import.meta.server ? query : '', async () => {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      }

      // Add preview token if available
      if (options.previewToken) {
        headers['X-Craft-Token'] = options.previewToken
      }

      // Client: call our server endpoint (secrets never reach the browser).
      // Server (SSR/SSG/prerender): call Craft directly using private runtimeConfig.
      const isServer = import.meta.server

      const url = isServer ? (() => {
        const craftUrl = String(config['CRAFT_URL'] || '').trim().replace(/\/+$/g, '')
        const token = String(config['GRAPHQL_TOKEN'] || '').trim()
        if (!craftUrl) throw new Error('CRAFT_URL is not configured')
        if (!token) throw new Error('GRAPHQL_TOKEN is not configured')
        headers['X-Craft-Authorization'] = `Bearer ${token}`
        return `${craftUrl}/api`
      })() : '/api/graphql'

      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          query,
          variables
        }),
        credentials: 'include',
        mode: 'cors'
      })

      if (!response.ok) {
        const errorText = await response.text().catch(() => '')
        const details = errorText ? ` - ${errorText.slice(0, 800)}` : ''
        const errorMessage = `HTTP error! status: ${response.status}${details}`
        throw Object.assign(new Error(errorMessage), { status: response.status })
      }

      const result: GraphQLResponse<T> = await response.json()

      if (!result || typeof result !== 'object') {
        throw new Error('Invalid response format')
      }

      if (result.errors) {
        const errorMessage = result.errors[0]?.message || 'GraphQL error'
        throw new Error(errorMessage)
      }

      const data = JSON.parse(JSON.stringify(result.data)) as T

      // Check if entry exists in response but is null (page not found)
      // During SSG generation, stop deployment if page is not found
      const isGenerating = typeof window === 'undefined' && process.env['NODE_ENV'] === 'production'
      const returnUri = variables['uri']
      
      if (isGenerating && returnUri && (data as any)?.entry === null) {
        console.error('❌ Build failed: Page not found during SSG generation')
        console.error(`   URI: ${returnUri}`)
        console.error(`   Site: ${variables['site']}`)
        console.error(`   Response data:`, data)
        throw new Error(`Build failed: Page not found for URI "${returnUri}". This indicates a missing page.`)
      }

      return data
    })
  }

  return { query }
}
